// "Who's racing?" — the shared-device profile switcher. Never forced on launch:
// opened from the active-player chip or the "Post as ▾" control. Lets a player
// switch, add a sibling, play as guest, or restore a claimed profile.
import { useState } from "react";
import { restoreProfile } from "../lib/api";
import {
  AVATARS,
  MAX_PROFILES,
  addProfile,
  importIdentity,
  listProfiles,
  removeProfile,
  setActive,
  type LocalProfile,
} from "../lib/profile";

type Mode = "list" | "add" | "restore";

export default function ProfileChooser({
  onClose,
  onChange,
  onViewProfile,
}: {
  onClose: () => void;
  /** Fired with the now-active profile (or null for guest). */
  onChange: (active: LocalProfile | null) => void;
  onViewProfile?: (profile: LocalProfile) => void;
}) {
  const [profiles, setProfiles] = useState<LocalProfile[]>(() => listProfiles());
  const [mode, setMode] = useState<Mode>(() =>
    listProfiles().length === 0 ? "add" : "list",
  );
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [handle, setHandle] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function pick(p: LocalProfile) {
    setActive(p.localId);
    onChange(p);
    onClose();
  }

  function guest() {
    setActive(null);
    onChange(null);
    onClose();
  }

  function create() {
    const p = addProfile(name, avatar);
    if (!p) {
      setErr(`That's the max of ${MAX_PROFILES} racers on this device.`);
      return;
    }
    onChange(p);
    onClose();
  }

  function remove(p: LocalProfile) {
    removeProfile(p.localId);
    setProfiles(listProfiles());
  }

  async function restore() {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const id = await restoreProfile(handle.trim(), passphrase);
      const p = importIdentity(id);
      onChange(p);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't restore that profile.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Who's racing?"
      onClick={onClose}
    >
      <div
        className="panel w-full max-w-sm p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-2xl text-ink">
            {mode === "add"
              ? "Add a racer"
              : mode === "restore"
                ? "Restore a profile"
                : "Who's racing?"}
          </h2>
          <button
            type="button"
            className="icon-btn"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {mode === "list" && (
          <>
            <ul className="space-y-2">
              {profiles.map((p) => (
                <li key={p.localId} className="flex items-center gap-2">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl border-[3px] border-ink/15 bg-white px-3 py-2.5 text-left hover:border-flame"
                    onClick={() => pick(p)}
                  >
                    <span className="text-2xl" aria-hidden>
                      {p.avatar || "🙂"}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-bold text-ink">
                        {p.displayName}
                      </span>
                      {p.handle && (
                        <span className="block truncate text-xs font-semibold text-ink/45">
                          @{p.handle}
                        </span>
                      )}
                    </span>
                    {onViewProfile && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="shrink-0 text-xs font-bold text-grape hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewProfile(p);
                          onClose();
                        }}
                      >
                        Profile →
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="icon-btn shrink-0"
                    aria-label={`Remove ${p.displayName} from this device`}
                    title="Remove from this device"
                    onClick={() => remove(p)}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-4 grid gap-2.5">
              <button
                type="button"
                className="btn-chunk bg-grass py-2.5 text-white disabled:opacity-50"
                disabled={profiles.length >= MAX_PROFILES}
                onClick={() => {
                  setErr("");
                  setName("");
                  setAvatar(AVATARS[profiles.length % AVATARS.length]);
                  setMode("add");
                }}
              >
                ➕ Add a racer
              </button>
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  className="btn-chunk bg-white py-2.5 text-sm"
                  onClick={() => {
                    setErr("");
                    setMode("restore");
                  }}
                >
                  🔑 Restore
                </button>
                <button
                  type="button"
                  className="btn-chunk bg-white py-2.5 text-sm"
                  onClick={guest}
                >
                  👋 Play as guest
                </button>
              </div>
            </div>
            {profiles.length >= MAX_PROFILES && (
              <p className="mt-2 text-center text-xs font-semibold text-ink/45">
                {MAX_PROFILES} racers max on one device — remove one to add another.
              </p>
            )}
          </>
        )}

        {mode === "add" && (
          <>
            <input
              type="text"
              value={name}
              maxLength={16}
              autoFocus
              placeholder="Racer nickname"
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-2xl border-[3px] border-ink bg-white px-4 py-2.5 font-semibold text-ink outline-none placeholder:text-ink/30 focus:border-flame"
            />
            <p className="mt-1.5 text-xs font-medium text-ink/45">
              Pick a fun nickname — not your real name. It can show on public
              leaderboards.
            </p>
            <div className="mt-3 text-xs font-bold uppercase tracking-wide text-ink/60">
              Pick an avatar
            </div>
            <div className="mt-2 grid grid-cols-6 gap-2">
              {AVATARS.map((a) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={avatar === a}
                  className={`rounded-xl border-[3px] py-1.5 text-2xl ${
                    avatar === a ? "border-flame bg-flame/10" : "border-ink/15 bg-white"
                  }`}
                  onClick={() => setAvatar(a)}
                >
                  {a}
                </button>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2.5">
              {profiles.length > 0 && (
                <button
                  type="button"
                  className="btn-chunk bg-white py-2.5 text-sm"
                  onClick={() => setMode("list")}
                >
                  ← Back
                </button>
              )}
              <button
                type="button"
                className={`btn-chunk bg-grass py-2.5 text-white ${
                  profiles.length > 0 ? "" : "col-span-2"
                }`}
                onClick={create}
              >
                🏁 Start racing
              </button>
            </div>
          </>
        )}

        {mode === "restore" && (
          <>
            <p className="mb-3 text-sm font-semibold text-ink/60">
              Enter the handle and recovery word you set when you claimed your
              profile.
            </p>
            <div className="space-y-2.5">
              <input
                type="text"
                value={handle}
                autoFocus
                placeholder="handle"
                autoComplete="off"
                onChange={(e) => setHandle(e.target.value)}
                className="w-full rounded-2xl border-[3px] border-ink bg-white px-4 py-2.5 font-semibold text-ink outline-none placeholder:text-ink/30 focus:border-flame"
              />
              <input
                type="text"
                value={passphrase}
                placeholder="recovery word"
                autoComplete="off"
                onChange={(e) => setPassphrase(e.target.value)}
                className="w-full rounded-2xl border-[3px] border-ink bg-white px-4 py-2.5 font-semibold text-ink outline-none placeholder:text-ink/30 focus:border-flame"
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2.5">
              <button
                type="button"
                className="btn-chunk bg-white py-2.5 text-sm"
                onClick={() => setMode(profiles.length ? "list" : "add")}
              >
                ← Back
              </button>
              <button
                type="button"
                className="btn-chunk bg-grape py-2.5 text-white disabled:opacity-50"
                disabled={busy}
                onClick={restore}
              >
                {busy ? "Restoring…" : "🔑 Restore"}
              </button>
            </div>
          </>
        )}

        {err && <p className="mt-3 text-center text-xs font-bold text-flame">{err}</p>}
      </div>
    </div>
  );
}
